import React from "react";

export const fmt = (n, d = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

export function DemoTag() {
  return null;
}

export function LiveDot({ label = "Live" }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-400">
      <span className="live-dot w-1.5 h-1.5 rounded-full" style={{ background: "var(--bull)" }} />
      {label}
    </span>
  );
}

const biasColors = {
  Bullish: "text-bull border-[color:var(--bull)]",
  Bearish: "text-bear border-[color:var(--bear)]",
  Neutral: "text-slate-400 border-slate-600",
  Ranging: "text-slate-400 border-slate-600",
};

export function BiasBadge({ bias }) {
  const c = biasColors[bias] || biasColors.Neutral;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-mono font-semibold uppercase border ${c}`} style={{ background: "rgba(255,255,255,0.02)" }}>
      {bias}
    </span>
  );
}

const statusColors = {
  "Published": "#3B82F6", "Active": "#10B981", "Waiting for Entry": "#F59E0B",
  "TP1 Hit": "#10B981", "TP2 Hit": "#059669", "TP3 Hit": "#047857",
  "Stop Loss Hit": "#EF4444", "Cancelled": "#64748B", "Closed": "#94A3B8",
  "Pending Approval": "#A855F7", "Expired": "#64748B", "Breakeven": "#64748B",
  "Setup Developing": "#F59E0B", "Detected": "#F59E0B",
};

export function StatusBadge({ status }) {
  const c = statusColors[status] || "#64748B";
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-mono uppercase tracking-wide border" style={{ color: c, borderColor: c, background: `${c}14` }}>
      {status}
    </span>
  );
}

export function StatCard({ label, value, sub, accent, testid, icon }) {
  const color = accent === "gold" ? "var(--gold-text)" : accent === "btc" ? "var(--btc-text)" : accent === "bull" ? "var(--bull)" : accent === "bear" ? "var(--bear)" : "var(--txt-primary)";
  return (
    <div data-testid={testid} className="surface surface-hover p-4 transition-colors duration-200 flex flex-col justify-between min-h-[92px]">
      <div className="flex items-start justify-between">
        <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
        {icon}
      </div>
      <div className="mt-2 font-mono text-2xl font-semibold tracking-tight" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export function Panel({ title, children, right, className = "", testid }) {
  return (
    <div data-testid={testid} className={`surface ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-subtle)]">
          <h3 className="text-sm font-head font-medium text-slate-200 tracking-tight">{title}</h3>
          {right}
        </div>
      )}
      <div>{children}</div>
    </div>
  );
}

export function Spinner({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center">
      <span className="w-4 h-4 border-2 border-slate-600 border-t-gold rounded-full animate-spin" />
      {label}…
    </div>
  );
}

export function AiText({ text }) {
  if (!text) return null;
  const lines = String(text).split("\n").filter((l) => l.trim() && !/^\s*\|?[\s:|-]*-{3,}[\s:|-]*$/.test(l) && !/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(l))
    .map((l) => (/^\s*\|.*\|\s*$/.test(l) ? l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()).filter(Boolean).join("  ·  ") : l));
  const tidy = (s) => s.replace(/\*\*/g, "").replace(/(^|\s)[*_]([^*_\n]+)[*_](?=\s|$|[.,:;!?])/g, "$1$2").replace(/^>\s*/, "");
  return (
    <div className="space-y-1.5 text-sm text-slate-300 leading-relaxed">
      {lines.map((l, i) => {
        const isBullet = /^([-•]|\*(?!\*))/.test(l.trim());
        const clean = (isBullet ? l.trim().replace(/^[-*•]\s*/, "") : l.trim()).replace(/^#+\s*/, "");
        const isHead = /^(#+|\*\*)/.test(l.trim()) || /^[A-Z][A-Za-z ]+:$/.test(l.trim());
        if (isHead && !isBullet) return <div key={i} className="font-semibold text-slate-100 pt-1">{tidy(clean)}</div>;
        return <div key={i} className={isBullet ? "flex gap-2" : ""}>{isBullet && <span className="text-gold">›</span>}<span>{tidy(clean)}</span></div>;
      })}
    </div>
  );
}
